// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ReviewListCard.test.tsx
import { CircleNotch, Image as ImageIcon } from "@phosphor-icons/react";
import { Link as RouterLink } from "@tanstack/react-router";
import cx from "classnames";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ArtworkPreviewThumb } from "./ArtworkPreviewThumb";
import { normalizeThumbnailUrl } from "./resolveReviewListCardThumbnail";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../../routes/ask-puntt/agentComponents/chatAtomRegistry";
import { VersionApprovalBadgeView } from "../../routes/ask-puntt/components/VersionApprovalBadge";
import { type VersionApproval } from "../../routes/ask-puntt/utils/versionApproval";
import { UnreadDot } from "../ask-puntt-list/UnreadDot";
import { interactiveSurfaceIconClasses } from "../InteractiveSurface";

/** Outline-style Review control — puntt tokens only; never white-on-white hover. */
export function reviewButtonClassName(isOpen = false): string {
  return cx(
    "inline-flex h-6 shrink-0 cursor-pointer items-center justify-center rounded-md border px-2 text-[12px] font-medium no-underline outline-none transition-colors",
    "border-puntt-neutral-gray-6 bg-base-white text-puntt-neutral-gray-11",
    "hover:border-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-2 hover:text-puntt-neutral-gray-12",
    "focus-visible:ring-2 focus-visible:ring-puntt-accent-8",
    isOpen &&
      "border-puntt-accent-7 bg-puntt-blue-2 text-puntt-neutral-gray-12",
  );
}

export type ReviewListCardItem = {
  id: string;
  name: string;
  versionNumber?: number;
  /** The newest of two or more versions of the same artwork. */
  isLatest?: boolean;
  thumbnailUrl?: string | null;
  /**
   * Artwork PDF to rasterize when `thumbnailUrl` is missing. The card keeps
   * the placeholder until page 1 has been drawn.
   */
  artworkPreviewUrl?: string | null;
  status?: string;
  aiCommentCount?: number;
  humanCommentCount?: number;
  disabled?: boolean;
  isGenerating?: boolean;
  /** Human approval decision on this version (agent-recorded), when decided. */
  approval?: VersionApproval | null;
  /**
   * Secondary line in place of the comment counts. A cited finding names the
   * issue here, under the review title, the way a count sits under a title.
   */
  detail?: string;
};

/**
 * Shared review card used by project and standalone chat review rails.
 * Optional `children` (e.g. per-review Participation) render below the
 * open-review control so we never nest interactive controls inside a button.
 */
export function ReviewListCard({
  item,
  onClick,
  testIdPrefix = "project-review-card",
  "data-testid": dataTestId,
  isActive,
  isHighlighted = false,
  hasUnread = false,
  unreadTestId,
  children,
  variant = "row",
  onReviewClick,
  reviewCtaTestId,
  reviewCtaOpen = false,
  to,
  params,
  ariaLabel: ariaLabelOverride,
}: {
  item: ReviewListCardItem;
  onClick?: () => void;
  /** Prefix for `data-testid` attributes, e.g. `project-review-card` or `chat-review-card`. */
  testIdPrefix?: string;
  "data-testid"?: string;
  isActive?: boolean;
  /** Soft emphasis when the matching in-chat approval card is hovered/focused. */
  isHighlighted?: boolean;
  hasUnread?: boolean;
  unreadTestId?: string;
  children?: ReactNode;
  /**
   * `row` = whole-card open control (chat/sidebar).
   * `review-cta` = name cluster + separate Review button (home HoverCard pattern).
   */
  variant?: "row" | "review-cta";
  onReviewClick?: () => void;
  reviewCtaTestId?: string;
  reviewCtaOpen?: boolean;
  /** When set, the card is a link to this route instead of a button. */
  to?: string;
  params?: Record<string, string>;
  /** Replaces the generated name when the card's visible words are not just the title. */
  ariaLabel?: string;
}) {
  const { t } = useTranslation("ui");
  const cardTestId = dataTestId ?? `${testIdPrefix}-${item.id}`;
  const aiCommentCount = item.aiCommentCount ?? 0;
  const humanCommentCount = item.humanCommentCount ?? 0;
  const isGenerating = item.isGenerating ?? false;
  const disabled = item.disabled ?? isGenerating;
  const thumbnailUrl = normalizeThumbnailUrl(item.thumbnailUrl);
  const artworkPreviewUrl = item.artworkPreviewUrl?.trim() || undefined;
  const [thumbnailFailed, setThumbnailFailed] = useState(false);

  useEffect(() => {
    setThumbnailFailed(false);
  }, [thumbnailUrl]);

  const ariaLabel =
    ariaLabelOverride ??
    (isGenerating
      ? t("reviewListCard.ariaLabel.generating", { name: item.name })
      : isActive
        ? t("reviewListCard.ariaLabel.currentlyViewing", { name: item.name })
        : item.status
          ? t("reviewListCard.ariaLabel.openReviewWithStatus", {
              name: item.name,
              status: item.status,
            })
          : t("reviewListCard.ariaLabel.openReview", { name: item.name }));

  const rowOnlyAgentId =
    variant !== "review-cta" && !children
      ? agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewListCard)
      : {};

  const controlClassName = cx(
    "flex flex-col gap-1 text-left disabled:cursor-not-allowed disabled:opacity-60",
    variant === "review-cta" ? "min-w-0 flex-1 p-2" : "w-full p-2",
    variant === "review-cta"
      ? "rounded-none border-0 bg-transparent shadow-none"
      : "rounded-xl border border-puntt-neutral-gray-7 bg-base-white shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]",
    !disabled && variant !== "review-cta" && "hover:border-puntt-accent-7",
    isActive && !children && "ring-2 ring-puntt-accent-8 ring-offset-1",
    isHighlighted &&
      !children &&
      "z-10 border-puntt-accent-7 bg-puntt-blue-2 shadow-md ring-2 ring-puntt-accent-8/60",
    children && "rounded-none border-0 shadow-none",
  );

  const cardFace = (
    <div
      className="flex items-center gap-2"
      data-testid={`${testIdPrefix}-${item.id}-row`}
    >
      <span
        aria-hidden="true"
        data-testid={`${testIdPrefix}-${item.id}-thumbnail`}
        className={interactiveSurfaceIconClasses(
          "flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border border-puntt-neutral-gray-4 bg-puntt-red-9 text-base-white",
        )}
      >
        {thumbnailUrl && !thumbnailFailed ? (
          // eslint-disable-next-line jsx-a11y/alt-text -- decorative thumbnail; the card carries an aria-label
          <img
            src={thumbnailUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="size-full object-cover"
            onError={() => setThumbnailFailed(true)}
          />
        ) : isGenerating ? (
          <CircleNotch size={18} className="animate-spin" aria-hidden="true" />
        ) : artworkPreviewUrl ? (
          <ArtworkPreviewThumb
            sourceUrl={artworkPreviewUrl}
            testId={`${testIdPrefix}-${item.id}-artwork-thumb`}
          />
        ) : (
          <ImageIcon size={18} weight="fill" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="block truncate text-[13px] font-medium text-puntt-neutral-gray-12">
            {item.name}
          </span>
          {hasUnread ? (
            <UnreadDot
              testId={unreadTestId ?? `chat-artifact-unread-${item.id}`}
              ariaLabel={t("reviewListCard.newReview")}
            />
          ) : null}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-2">
          {item.versionNumber != null && (
            <span
              className="inline-block rounded-sm bg-puntt-neutral-gray-3 px-2 py-0.5 text-[12px] font-medium text-puntt-neutral-gray-11"
              data-testid={`${testIdPrefix}-${item.id}-version`}
            >
              V{item.versionNumber}
            </span>
          )}
          {item.isLatest ? (
            <span
              className="inline-block rounded-sm bg-puntt-blue-2 px-2 py-0.5 text-[12px] font-medium text-puntt-blue-11"
              data-testid={`${testIdPrefix}-${item.id}-latest`}
            >
              {t("reviewListCard.latest")}
            </span>
          ) : null}
          {item.approval != null && (
            <span
              className="inline-flex"
              data-testid={`${testIdPrefix}-${item.id}-approval`}
            >
              <VersionApprovalBadgeView approval={item.approval} />
            </span>
          )}
          {item.detail ? (
            <span
              className="line-clamp-2 text-[11px] leading-snug text-puntt-neutral-gray-11"
              data-testid={`${testIdPrefix}-${item.id}-detail`}
            >
              {item.detail}
            </span>
          ) : isGenerating ? null : (
            // why: a running review's comment total is provisional. The spinner stays; the count waits until generation finishes.
            <>
              <span
                className="inline-flex items-center text-[11px] text-puntt-neutral-gray-11"
                data-testid={`${testIdPrefix}-${item.id}-ai-comments`}
              >
                {aiCommentCount}{" "}
                {t("reviewListCard.aiCommentLabel", {
                  count: aiCommentCount,
                })}
              </span>
              {humanCommentCount > 0 && (
                <>
                  <span
                    aria-hidden="true"
                    className="text-[11px] text-puntt-neutral-gray-11"
                  >
                    •
                  </span>
                  <span
                    className="inline-flex items-center text-[11px] text-puntt-neutral-gray-11"
                    data-testid={`${testIdPrefix}-${item.id}-human-comments`}
                  >
                    {humanCommentCount}{" "}
                    {t("reviewListCard.humanCommentLabel", {
                      count: humanCommentCount,
                    })}
                  </span>
                </>
              )}
            </>
          )}
        </span>
      </span>
    </div>
  );

  const openControl = to ? (
    <RouterLink
      {...rowOnlyAgentId}
      to={to as never}
      params={(params ?? {}) as never}
      aria-label={ariaLabel}
      data-testid={cardTestId}
      className={`${controlClassName} no-underline`}
      data-highlighted={isHighlighted ? "true" : undefined}
    >
      {cardFace}
    </RouterLink>
  ) : (
    <button
      {...rowOnlyAgentId}
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-current={isActive ? "true" : undefined}
      onClick={() => {
        if (!disabled) onClick?.();
      }}
      aria-label={ariaLabel}
      data-testid={cardTestId}
      className={controlClassName}
      data-highlighted={isHighlighted ? "true" : undefined}
    >
      {cardFace}
    </button>
  );

  if (variant === "review-cta") {
    const ctaTestId = reviewCtaTestId ?? `${testIdPrefix}-${item.id}-review`;
    return (
      <div
        {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewListCard)}
        className="flex items-center gap-2 rounded-xl border border-puntt-neutral-gray-7 bg-base-white pr-2 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]"
        data-testid={`${testIdPrefix}-${item.id}-review-cta`}
      >
        {openControl}
        <button
          type="button"
          disabled={disabled}
          className={reviewButtonClassName(reviewCtaOpen)}
          data-testid={ctaTestId}
          aria-label={t("homeTaskList.reviewAsset", { title: item.name })}
          onClick={() => {
            if (!disabled) onReviewClick?.();
          }}
        >
          {t("homeTaskList.review")}
        </button>
      </div>
    );
  }

  if (!children) {
    return openControl;
  }

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewListCard)}
      className={cx(
        "rounded-xl",
        isActive && "ring-2 ring-puntt-accent-8 ring-offset-1",
        isHighlighted &&
          "z-10 scale-[1.03] shadow-md ring-2 ring-puntt-accent-8/60 motion-reduce:scale-100",
      )}
      data-testid={`${testIdPrefix}-${item.id}-shell`}
      data-highlighted={isHighlighted ? "true" : undefined}
    >
      <div
        className={cx(
          "flex flex-col overflow-hidden rounded-xl border border-puntt-neutral-gray-5 bg-base-white",
          isHighlighted && "border-puntt-accent-7",
        )}
        data-testid={`${testIdPrefix}-${item.id}-shell-inner`}
      >
        {openControl}
        {children}
      </div>
    </div>
  );
}
